import { BaseResponse, TableList } from '@/types/common';
import axiosInstance from './axiosInstance';

const baseUrl = `${SUPP_ADMIN_BASE_URL}/supp-management`;
const shoppingMallBaseUrl = `${SUPP_ADMIN_BASE_URL}/shopping-mall`;

// 枚举定义
export enum ProductStockStatus {
  OffShelf = 0,
  OnShelf = 1,
}

export enum PayType {
  Points = 1,
  Cash,
  Coin,
  Givs,
  Member = 5, // 会员专属
  VipMember = 6, // 高级vip专属
}

export const PayTypeMap = new Map([
  [PayType.Points, '积分'],
  [PayType.Cash, '现金'],
  [PayType.Coin, '充值金币'],
  [PayType.Givs, '活动金币'],
  [PayType.Member, '会员专属'],
  [PayType.VipMember, '高级vip专属'],
]);

export enum PointsOpType {
  Decrease = 0,
  Increase = 1,
}

// 接口类型定义
export interface ProductImage {
  imageUrl: string;
  ratio: number;
}

export interface ProductCategory {
  productCategoryId: number;
  categoryCode: string | null;
  categoryTitle: string;
  categoryDesc: string | null;
  parentId: number;
  categoryPath: string | null;
  categoryOrder: number;
  children?: ProductCategory[];
}

export interface ProductStock {
  productStockId: number;
  productShowImages: string | ProductImage[];
  productTitle: string;
  productImages: string | ProductImage[];
  productCategoryId: number;
  categoryTitle: string;
  payType: PayType;
  price: number;
  stock: number;
  productStockStatus: ProductStockStatus;
  createUserId: number;
  createUser: string;
  createTime: number;
}

export interface ProductStockAddParams {
  productCategoryId: number;
  productShowImages: ProductImage[];
  productTitle: string;
  productImages: ProductImage[];
  payType: PayType;
  price: number;
  stock: number;
  num: number;
  prePersonLimit: number;
  shippingInformation: string;
  disclaimer: string;
  productDesc: string;
  productExtData: string;
}

export interface ProductStockListParams {
  productStockId?: number;
  productTitle?: string;
  productStockStatus?: ProductStockStatus;
  productCategoryId?: number;
  currentPage: number;
  pageSize: number;
}

export interface ProductUpdateInfoParams {
  productStockId: number;
  productCategoryId: number;
  productShowImages: ProductImage[];
  productTitle: string;
  productImages: ProductImage[];
  productDesc: string;
  productExtData: string;
}

export interface ProductUpdateStockParams {
  productStockId: number;
  payType: PayType;
  price: number;
  stock: number;
  num: number;
  prePersonLimit: number;
}

export interface ProductUpdateShippingParams {
  productStockId: number;
  shippingInformation: string;
  disclaimer: string;
}

export interface ProductUpdateStatusParams {
  productStockId: number;
  productStockStatus: ProductStockStatus;
}

export interface ProductHistoryItem {
  fieldType: number;
  updateBefore: string;
  updateAfter: string;
  createTime: number;
  createUserId: number;
  createUser: string;
}

export interface ProductTag {
  text: string;
}

export interface AppProductStock {
  productStockId: number;
  productTags: ProductTag[];
  productTitle: string;
  productShowImages: ProductImage[];
  productImages: ProductImage[];
  productDesc: string;
  payType: PayType;
  price: number;
  createTime: number;
}

export interface ProductDetail {
  productStockId: number;
  productTags: ProductTag[];
  productTitle: string;
  productShowImages: ProductImage[];
  productImages: ProductImage[];
  productDesc: string;
  productExtData: Array<{
    text: string | null;
    image: ProductImage;
  }>;
  shippingInformation: string;
  disclaimer: string;
  productPoints: number;
  availablePoints: number;
  stock: number;
  createTime: number;
}

export interface UserPointsRecord {
  userId: number;
  pointsSense: number;
  pointsSenseTitle?: string;
  pointsSenseDesc?: string;
  pointsSenseIconUrl?: string;
  source: number | null;
  pointsOpType: PointsOpType;
  points: number;
  bizId: number;
  extData: any;
  createTime: string;
}

export interface UserPointsInfo {
  totalPoints: number;
}

// API 函数
/**
 * 获取通用选项（支付类型、更新字段类型等）
 * @returns 支付类型和产品更新字段类型列表
 */
export const getCommonOptions = async () => {
  const res = await axiosInstance.get<{
    payTypes: { value: number; text: string }[];
    productUpdateFieldTypes: { value: number; text: string }[];
  }>(`${baseUrl}/common/options`);
  return res.data;
};

/**
 * 获取商品分类列表
 * @param type - 分类返回类型（1：平铺返回分类）
 * @returns 商品分类列表
 */
export const getProductCategories = async (type?: number) => {
  const res = await axiosInstance.get<ProductCategory[]>(
    `${baseUrl}/product/category/list${type ? `?type=${type}` : ''}`
  );
  return res.data;
};

/**
 * 添加商品库存
 * @param params - 商品库存信息
 * @returns 是否添加成功
 */
export const addProductStock = async (params: ProductStockAddParams) => {
  const res = await axiosInstance.post<boolean>(
    `${baseUrl}/product/stock/add`,
    params
  );
  return res.data;
};

/**
 * 获取商品库存列表
 * @param params - 查询参数
 * @returns 商品库存列表及分页信息
 */
export const getProductStockList = async (params: ProductStockListParams) => {
  const res = await axiosInstance.post<TableList<ProductStock>>(
    `${baseUrl}/product/stock/list`,
    params
  );
  return res.data;
};

/**
 * 更新商品基本信息
 * @param params - 商品基本信息
 * @returns 是否更新成功
 */
export const updateProductInfo = async (params: ProductUpdateInfoParams) => {
  const res = await axiosInstance.post<boolean>(
    `${baseUrl}/product/update/info`,
    params
  );
  return res.data;
};

/**
 * 更新商品库存信息
 * @param params - 商品库存信息
 * @returns 是否更新成功
 */
export const updateProductStock = async (params: ProductUpdateStockParams) => {
  const res = await axiosInstance.post<boolean>(
    `${baseUrl}/product/update/stock`,
    params
  );
  return res.data;
};

/**
 * 更新商品物流信息
 * @param params - 商品物流信息
 * @returns 是否更新成功
 */
export const updateProductShipping = async (
  params: ProductUpdateShippingParams
) => {
  const res = await axiosInstance.post<boolean>(
    `${baseUrl}/product/update/shipping`,
    params
  );
  return res.data;
};

/**
 * 更新商品上下架状态
 * @param params - 商品状态信息
 * @returns 是否更新成功
 */
export const updateProductStatus = async (
  params: ProductUpdateStatusParams
) => {
  const res = await axiosInstance.post<boolean>(
    `${baseUrl}/product/update/stock/status`,
    params
  );
  return res.data;
};

/**
 * 获取商品操作历史列表
 * @param params - 查询参数
 * @returns 商品操作历史列表及分页信息
 */
export const getProductHistory = async (params: {
  productStockId: number;
  fieldType?: number;
  currentPage: number;
  pageSize: number;
}) => {
  const res = await axiosInstance.post<TableList<ProductHistoryItem>>(
    `${baseUrl}/product/update/history/list`,
    params
  );
  return res.data;
};

// App端接口
/**
 * 获取App端商品列表
 * @param params - 分页参数
 * @returns 商品列表及分页信息
 */
export const getAppProductList = async (params: {
  currentPage: number;
  pageSize: number;
}) => {
  const res = await axiosInstance.get<TableList<AppProductStock>>(
    `${shoppingMallBaseUrl}/api/product/v1/stock/list`,
    { params }
  );
  return res.data;
};

/**
 * 获取商品详情
 * @param productStockId - 商品库存ID
 * @returns 商品详情信息
 */
export const getProductDetail = async (productStockId: number) => {
  const res = await axiosInstance.get<ProductDetail[]>(
    `${baseUrl}/api/product/detail`,
    {
      params: { productStockId },
    }
  );
  return res.data;
};

// 用户积分相关接口
/**
 * 获取用户积分记录
 * @param params - 查询参数
 * @returns 用户积分记录列表及分页信息
 */
export const getUserPointsRecords = async (params: {
  pointsType?: PointsOpType;
  currentPage: number;
  pageSize: number;
}) => {
  const res = await axiosInstance.get<TableList<UserPointsRecord>>(
    `${baseUrl}/user/points/records`,
    { params }
  );
  return res.data;
};

/**
 * 调整用户积分
 * @param params - 积分调整参数
 * @returns 是否调整成功
 */
export const adjustUserPoints = async (params: {
  userId: number;
  pointsType: PointsOpType;
  points: number;
}) => {
  const res = await axiosInstance.post<boolean>(
    `${baseUrl}/user/points/adjust`,
    params
  );
  return res.data;
};

/**
 * 获取App端用户积分列表
 * @param params - 查询参数
 * @returns 用户积分记录列表及分页信息
 */
export const getAppUserPointsList = async (params: {
  pointsOpType?: PointsOpType;
  currentPage: number;
  pageSize: number;
}) => {
  const res = await axiosInstance.get<TableList<UserPointsRecord>>(
    `${shoppingMallBaseUrl}/api/userPoints/v1/list`,
    { params }
  );
  return res.data;
};

/**
 * 获取用户积分信息
 * @returns 用户积分信息
 */
export const getUserPointsInfo = async () => {
  const res = await axiosInstance.get<UserPointsInfo>(
    `${shoppingMallBaseUrl}/api/userPoints/v1/points/info`
  );
  return res.data;
};
